import React, { useState, MouseEvent, useRef, useLayoutEffect, RefObject, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { clsx } from 'clsx';

import { lang } from '@ott/l10n';
import { useLayoutContext } from '@ott/layout-context';
import { authActions } from '@ott/solar-auth';

import { getAuthSelector } from 'src/redux/selectors/commonSelectors';

import FadeIn from 'src/components/elements/FadeIn';
import GradientButton from 'src/components/elements/GradientButton';

import metrics from 'src/utility/metrics';

import { isUserAuthenticated } from 'src/helpers/user';
import useIntersection from 'src/hooks/useIntersection';
import useIsWebview from 'src/hooks/useIsWebview';
import useTypedSelector from 'src/hooks/useTypedSelector';
import { AchievementType } from 'src/types';

import { ACHIEVEMENTS } from './constants';

import { motion } from 'framer-motion';
import { gsap } from 'gsap';

import styles from './Achievements.scss';

const Achievements = () => {
  const [onHover, setOnHover] = useState(false);
  const [currentCardId, setCurrentCardId] = useState(null);

  const cardRef: RefObject<HTMLDivElement> = useRef(null);
  const containerRef: RefObject<HTMLDivElement> = useRef(null);
  const achievementsCards: RefObject<HTMLDivElement> = useRef(null);

  const { isDesktop } = useLayoutContext();
  const inViewport = useIntersection({ element: containerRef, rootMargin: '-10%' });
  const auth = useTypedSelector(getAuthSelector);
  const dispath = useDispatch();

  const isWebview = useIsWebview();
  const webView = isWebview && !isDesktop;
  const webViewTarget = webView ? '_self' : '_blank';

  const achievementsData: AchievementType[] = isWebview
    ? ACHIEVEMENTS.filter((el) => !el.withoutWebView)
    : ACHIEVEMENTS;

  useLayoutEffect(() => {
    const cards = gsap.utils.toArray('#achievement-card');

    if (inViewport) {
      gsap.fromTo(cards, { opacity: 0, x: 50 }, { opacity: 1, x: 0, stagger: 0.0625, delay: 1.5 });
    }
  }, [inViewport]);

  const handleClick = (achievement: AchievementType) => {
    metrics('SOLAR_LANDING_REWARDS_ACHIEVEMENT', { title: achievement.title });
    const appLink = webView ? achievement.CTAButtonMobileLink : achievement.buttonAction;
    const isWelcomeAchievement = achievement.id === 1;

    if (isWelcomeAchievement) {
      if (isUserAuthenticated(auth) || isWebview) {
        window.open(appLink, '_blank');
      } else {
        dispath(authActions.showAuth());
      }
    } else {
      window.open(appLink, webViewTarget);
    }
  };

  const onCheckCollectiblesClick = (event) => {
    metrics('SOLAR_LANDING_REWARDS_BUTTON_HOW2');
    event.preventDefault();
    const link = webView
      ? 'solartrip://solartrip.com/profile/achievements'
      : `/${lang}/profile/achievements`;
    window.open(link, webViewTarget);
  };

  let cursorPosition = { top: 0, left: 0, x: 0, y: 0 };

  const mouseMoveHandler = (e) => {
    const dx = e.clientX - cursorPosition.x;
    const dy = e.clientY - cursorPosition.y;

    achievementsCards.current!.scrollTop = cursorPosition.top - dy;
    achievementsCards.current!.scrollLeft = cursorPosition.left - dx;
  };

  const mouseDownHandler = (e) => {
    cursorPosition = {
      left: achievementsCards.current!.scrollLeft,
      top: achievementsCards.current!.scrollTop,
      x: e.clientX,
      y: e.clientY,
    };

    achievementsCards.current!.style.cursor = 'grabbing';
    achievementsCards.current!.style.userSelect = 'none';

    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  };

  const mouseUpHandler = () => {
    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler);

    achievementsCards.current!.style.cursor = 'grab';
    achievementsCards.current!.style.removeProperty('user-select');
  };

  useEffect(() => {
    if (achievementsCards.current) {
      achievementsCards.current.addEventListener('mousedown', mouseDownHandler);

      return () => {
        if (achievementsCards.current) {
          achievementsCards.current.addEventListener('mousedown', mouseDownHandler);
        }
      };
    }
  }, []);

  return (
    <div id="achievements-cards" ref={containerRef} className={styles.achievementsWrapper}>
      <FadeIn className={styles.title}>
        <span id="explore-nft">Start your collection</span>
      </FadeIn>
      <FadeIn className={styles.description} delay={1}>
        <span>You'll earn planet rewards for the following actions</span>
      </FadeIn>
      {isDesktop ? (
        <div ref={achievementsCards} className={styles.achievements}>
          {achievementsData.map((achievement) => (
            <div
              ref={cardRef}
              id="achievement-card"
              key={achievement.id}
              className={styles.achievementCard}
              data-info={JSON.stringify(achievement)}
              onMouseEnter={(event: MouseEvent<HTMLDivElement>) => {
                const target = event.target as HTMLDivElement;

                if (target?.dataset?.info) {
                  setCurrentCardId(JSON.parse(target.dataset.info).id);
                  setOnHover(true);
                }
              }}
              onMouseLeave={() => setOnHover(false)}
            >
              {onHover && currentCardId === achievement.id && (
                <motion.video
                  autoPlay={true}
                  preload="auto"
                  loop={true}
                  muted={true}
                  playsInline={true}
                  className={styles.video}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.7 }}
                  transition={{ duration: 0.3 }}
                >
                  <source src={achievement.cardBg} type="video/mp4" />
                </motion.video>
              )}
              <h2 className={styles.cardTitle}>{achievement.title}</h2>
              <div className={styles.cardDescription}>{achievement.description}</div>
              <button
                type="button"
                className={clsx(styles.cardAction, {
                  [styles.cardActionTransparency]: onHover && currentCardId === achievement.id,
                })}
                onClick={() => handleClick(achievement)}
              >
                {achievement.buttonCaption}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.achievements}>
          {achievementsData.map((achievement) => {
            return (
              <div ref={cardRef} key={achievement.id} className={styles.achievementCard}>
                <h2 className={styles.cardTitle}>{achievement.title}</h2>
                <div className={styles.cardDescription}>{achievement.description}</div>
                <button
                  type="button"
                  className={styles.cardAction}
                  onClick={() => handleClick(achievement)}
                >
                  {achievement.buttonCaption}
                </button>

                <motion.video
                  autoPlay
                  preload="auto"
                  loop
                  muted
                  playsInline
                  className={styles.video}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.7 }}
                  transition={{ duration: 0.3 }}
                >
                  <source src={achievement.cardBg} type="video/mp4" />
                </motion.video>
              </div>
            );
          })}
        </div>
      )}

      <FadeIn delay={2}>
        <GradientButton
          className={styles.button}
          onClick={(event) => onCheckCollectiblesClick(event)}
        >
          Check my Planet collectibles
        </GradientButton>
      </FadeIn>
    </div>
  );
};

export default Achievements;
